import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { FiEdit2 } from 'react-icons/fi';
import EditEmployeeDialog from './EditEmployeeDialog';
import './Organization.css';

const apiUrl = process.env.REACT_APP_API_URL || '';

const Organization = ({
  managerName = 'Sudheer',
  usersEndpoint = null,
  isMainManager = false,
  allowEdit = false
}) => {
  const [users, setUsers] = useState([]);
  const [projectsCount, setProjectsCount] = useState(0);
  const [projectsList, setProjectsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit employee state
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const isEditingRef = useRef(false);
  isEditingRef.current = isEditModalOpen;

  // Determine if editing is enabled (Main Manager has edit access)
  const isMain = Boolean(
    isMainManager ||
    allowEdit ||
    localStorage.getItem('isMainManager') === '1'
  );

  const fetchData = useCallback(async () => {
    try {
      const endpoint = usersEndpoint || `${apiUrl}/api/users`;

      // Always fetch all projects to support projectsCount & edit dropdown
      const [usersRes, projectsRes] = await Promise.all([
        axios.get(endpoint),
        axios.get(`${apiUrl}/api/fetch-projects`).catch(() => ({ data: [] }))
      ]);

      const usersData = Array.isArray(usersRes.data) ? usersRes.data : [];
      const projsData = Array.isArray(projectsRes.data) ? projectsRes.data : [];

      setUsers(usersData);
      setProjectsList(projsData);

      if (usersEndpoint && !usersEndpoint.includes('/api/users')) {
        // Count unique projects among scoped users
        const uniqueProjects = new Set();
        usersData.forEach(u => {
          if (u.project_name) {
            u.project_name.split(',').forEach(p => uniqueProjects.add(p.trim()));
          }
        });
        setProjectsCount(uniqueProjects.size);
      } else {
        setProjectsCount(projsData.length);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching organization data:', err);
      setError('Failed to load organization data.');
      setLoading(false);
    }
  }, [usersEndpoint]);

  useEffect(() => {
    fetchData();

    // Live update polling interval (every 4 seconds) to reflect user/project additions & deletions
    const intervalId = setInterval(() => {
      // Do not refetch during modal edit to prevent state overwrites
      if (!isEditingRef.current) {
        fetchData();
      }
    }, 4000);

    const handleFocus = () => {
      if (!isEditingRef.current) {
        fetchData();
      }
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchData]);

  // Function to extract initials from full name
  const getInitials = (name) => {
    if (!name) return 'U';
    const names = name.trim().split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const formatRole = (role) => {
    if (!role) return 'Employee';
    const r = String(role).toLowerCase().trim();
    if (r === 'employee') return 'Employee';
    if (r === 'lead') return 'Project Lead';
    if (r === 'admin') return 'Manager / Admin';
    if (r === 'manager') return 'Manager';
    if (r === 'main_manager' || r === 'mainmanager') return 'Main Manager';
    if (r === 'hr') return 'HR';
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  const handleEditClick = (employee) => {
    setSelectedEmployee(employee);
    setIsEditModalOpen(true);
  };

  const handleCloseEdit = () => {
    setIsEditModalOpen(false);
    setSelectedEmployee(null);
  };

  const handleSaveEmployee = (updatedData) => {
    setUsers(prev => prev.map(u => {
      const matchEmp = (u.employee_number && u.employee_number === updatedData.employee_number) ||
                       (u.employee_id && u.employee_id === updatedData.employee_number) ||
                       (u._id && updatedData._id && u._id === updatedData._id);
      return matchEmp ? { ...u, ...updatedData } : u;
    }));
    fetchData();
    window.location.reload();
  };

  if (loading) {
    return <div className="organization-loading">Loading organization data...</div>;
  }

  if (error) {
    return <div className="organization-error">{error}</div>;
  }

  return (
    <div className="organization-container">
      {/* Top Header Row with Welcome Title & Stat Cards */}
      <div className="organization-header-row">
        <h1 className="organization-welcome-title">Hi {managerName}...</h1>
        <div className="organization-stats-container">
          <div className="org-stat-card">
            <div className="stat-text-group">
              <span className="stat-main-heading">Employees</span>
            </div>
            <span className="stat-value orange-count">{users.length}</span>
          </div>
          <div className="org-stat-card">
            <div className="stat-text-group">
              <span className="stat-main-heading">Projects</span>
            </div>
            <span className="stat-value orange-count">{projectsCount}</span>
          </div>
        </div>
      </div>

      {/* Employee Cards Grid */}
      <div className="organization-grid">
        {users.map((user, index) => (
          <div className="user-card" key={user._id || user.employee_number || index}>
            <div className="user-card-header">
              <div className="user-avatar">
                {user.profile_image ? (
                  <img src={user.profile_image} alt={user.full_name} className="avatar-img" />
                ) : (
                  <span className="avatar-initials">{getInitials(user.full_name)}</span>
                )}
              </div>
              <div className="user-info-primary">
                <div className="user-name-row">
                  <h3 className="user-name" title={user.full_name}>{user.full_name}</h3>
                  <div className="user-badges-group">
                    {user.on_leave && <span className="leave-badge">LEAVE</span>}
                    {isMain && (
                      <button
                        type="button"
                        className="user-card-quick-edit-btn"
                        onClick={() => handleEditClick(user)}
                        title={`Edit ${user.full_name}`}
                        aria-label={`Edit ${user.full_name}`}
                      >
                        <FiEdit2 size={13} />
                      </button>
                    )}
                  </div>
                </div>
                <p className="user-job-title" title={user.job_title}>{user.job_title || 'N/A'}</p>
              </div>
            </div>

            <div className="user-card-body">
              <div className="info-row">
                <span className="info-label">Role : </span>
                <span className="info-value">{formatRole(user.role)}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Location : </span>
                <span className="info-value">{user.location || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Department : </span>
                <span className="info-value">{user.department || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Project : </span>
                <span className="info-value" title={user.project_name || user.project || 'None'}>
                  {user.project_name || user.project || 'None'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Email : </span>
                <span className="info-value email-value" title={user.username || user.email}>
                  {user.username || user.email || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Employee Modal */}
      {isEditModalOpen && selectedEmployee && (
        <EditEmployeeDialog
          employee={selectedEmployee}
          projects={projectsList}
          onClose={handleCloseEdit}
          onSave={handleSaveEmployee}
        />
      )}
    </div>
  );
};

export default Organization;
